import * as Dialog from '@radix-ui/react-dialog';
import { Save, X, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface ModalCriarTemplateParteProps {
  aberto: boolean;
  onFechar: () => void;
  apreciacaoId: string;
  parteNome: string;
  projetoId?: string;
  onSucesso?: () => void;
}

export function ModalCriarTemplateParte({
  aberto,
  onFechar,
  apreciacaoId,
  parteNome,
  projetoId,
  onSucesso,
}: ModalCriarTemplateParteProps) {
  const { t } = useI18n();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleSalvar = async () => {
    if (!nome.trim()) {
      setErro(t('apreciacaoModais.criarTemplateParte.erroNomeObrigatorio'));
      return;
    }

    if (!projetoId) {
      setErro(t('apreciacaoModais.criarTemplateParte.erroProjeto'));
      return;
    }

    setErro(null);
    setSalvando(true);

    try {
      const response = await apiService.criarTemplateParteDeApreciacao(
        apreciacaoId,
        parteNome,
        {
          nome: nome.trim(),
          descricao: descricao.trim() || undefined,
        }
      );

      if (response.success) {
        setNome('');
        setDescricao('');
        if (onSucesso) {
          onSucesso();
        }
        onFechar();
      } else {
        setErro(response.message || t('apreciacaoModais.criarTemplateParte.erroCriar'));
      }
    } catch (error) {
      console.error('Erro ao criar template de parte:', error);
      setErro(t('apreciacaoModais.criarTemplateParte.erroCriar'));
    } finally {
      setSalvando(false);
    }
  };

  const handleFechar = () => {
    if (!salvando) {
      setNome('');
      setDescricao('');
      setErro(null);
      onFechar();
    }
  };

  return (
    <Dialog.Root open={aberto} onOpenChange={handleFechar}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[90vw] max-w-md z-50">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-bold text-gray-900">
                {t('apreciacaoModais.criarTemplateParte.titulo')}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  disabled={salvando}
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  <strong>{t('apreciacaoModais.criarTemplateParte.parteRotulo')}</strong> {parteNome}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {t('apreciacaoModais.criarTemplateParte.aviso')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apreciacaoModais.criarTemplateParte.nomeRotulo')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder={t('apreciacaoModais.criarTemplateParte.nomePlaceholder')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={salvando}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apreciacaoModais.criarTemplateParte.descricaoRotulo')}
                </label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder={t('apreciacaoModais.criarTemplateParte.descricaoPlaceholder')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[80px]"
                  rows={3}
                  disabled={salvando}
                />
              </div>

              {erro && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{erro}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
              <Dialog.Close asChild>
                <button
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={salvando}
                >
                  {t('comum.acao.cancelar')}
                </button>
              </Dialog.Close>
              <button
                onClick={handleSalvar}
                disabled={salvando || !nome.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {salvando ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('comum.acao.salvando')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('apreciacaoModais.criarTemplateParte.botaoCriar')}
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}






